import {
  ArrowUpRight,
  BookCheck,
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  Loader2,
  PlayCircle,
  Sparkles,
} from "lucide-react";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";

import { GrantFitBadge } from "@/components/grant_suite/GrantFitBadge";
import type { GrantFitTier } from "@/components/grant_suite/grantFitDisplay";
import {
  type DiscoverGrant,
  grantCardSurfaceClass,
  grantScopeMeta,
} from "@/components/grant_suite/grantDiscoverUi";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type GrantDiscoverGrantCardProps = {
  grant: DiscoverGrant;
  summarizing: boolean;
  summary?: string;
  error?: string;
  onSummarizeEligibility: (g: DiscoverGrant) => void;
  showWatchlistButton?: boolean;
  onSaveToWatchlist?: (g: DiscoverGrant) => void;
  onStartPursuing?: (g: DiscoverGrant) => void;
  watchlistSaving?: boolean;
  pursuingSaving?: boolean;
  onSaved?: boolean;
  onPursuing?: boolean;
  trackingDisabled?: boolean;
  fitTier?: GrantFitTier;
  showExplainFit?: boolean;
  fitExplanation?: string;
  explainingFit?: boolean;
  fitExplanationError?: string;
  onExplainFit?: (g: DiscoverGrant) => void;
  // ENG-1690: when this discovered grant is already in pursuit, its tracked
  // opportunity id lets the card deep-link into the tracker.
  trackedOpportunityId?: number;
  onOpenInTracker?: (opportunityId: number) => void;
};

export function GrantDiscoverGrantCard({
  grant,
  summarizing,
  summary,
  error,
  onSummarizeEligibility,
  showWatchlistButton = false,
  onSaveToWatchlist,
  onStartPursuing,
  watchlistSaving = false,
  pursuingSaving = false,
  onSaved = false,
  onPursuing = false,
  trackingDisabled = false,
  fitTier,
  showExplainFit = false,
  fitExplanation,
  explainingFit = false,
  fitExplanationError,
  onExplainFit,
  trackedOpportunityId,
  onOpenInTracker,
}: GrantDiscoverGrantCardProps) {
  const scope = grantScopeMeta(grant);
  const ScopeIcon = scope.icon;

  // ENG-1690: an already-pursued grant can be opened directly in the tracker.
  const canOpenInTracker = onPursuing && trackedOpportunityId != null && Boolean(onOpenInTracker);
  const openInTracker = () => {
    if (trackedOpportunityId != null) onOpenInTracker?.(trackedOpportunityId);
  };

  return (
    <Card
      className={cn(
        grantCardSurfaceClass(),
        canOpenInTracker && "cursor-pointer transition-colors hover:border-primary/50",
      )}
      onClick={
        canOpenInTracker
          ? (event) => {
              // Mouse convenience only — ignore clicks on inner controls
              // (buttons, links, accordion trigger). The "Open in tracker"
              // button below is the accessible/keyboard control.
              if ((event.target as HTMLElement).closest("button, a")) return;
              openInTracker();
            }
          : undefined
      }
    >
      <CardHeader className="space-y-3 p-5 pb-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className={cn("gap-1 font-normal", scope.badgeClass)}>
                <ScopeIcon className="h-3 w-3" />
                {scope.label}
              </Badge>
              {fitTier ? <GrantFitBadge tier={fitTier} /> : null}
            </div>
            <CardTitle className="text-base font-semibold leading-snug line-clamp-2">
              {grant.name}
            </CardTitle>
          </div>
          {grant.url ? (
            <Button variant="ghost" size="icon" asChild className="h-8 w-8 shrink-0">
              <a href={grant.url} target="_blank" rel="noopener noreferrer" title="Visit program site">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4 p-5 pt-3">
        <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
          {grant.description || "No description available for this program."}
        </p>

        {grant.eligibility ? (
          <Accordion type="single" collapsible>
            <AccordionItem value={`eligibility-${grant.id}`} className="border-none">
              <AccordionTrigger className="py-1 text-xs font-medium hover:no-underline">
                View eligibility requirements
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-xs leading-relaxed text-muted-foreground">{grant.eligibility}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : null}

        {fitExplanation ? (
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-50/50 p-3 text-xs dark:bg-emerald-950/20">
            <p className="mb-1.5 font-medium text-foreground">Why this fit?</p>
            <p className="leading-relaxed text-muted-foreground">{fitExplanation}</p>
          </div>
        ) : null}
        {fitExplanationError ? <p className="text-xs text-destructive">{fitExplanationError}</p> : null}

        {summary ? (
          <div className="rounded-lg border bg-muted/30 p-3 text-xs">
            <p className="mb-1.5 font-medium text-foreground">Who should apply?</p>
            <div className="prose prose-sm max-w-none text-muted-foreground prose-p:my-1 prose-p:text-xs">
              <SafeMarkdown remarkPlugins={[remarkGfm]}>{summary}</SafeMarkdown>
            </div>
          </div>
        ) : null}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}

        <div className="mt-auto flex flex-col gap-2 border-t border-border/60 pt-4">
          {showWatchlistButton ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={onPursuing ? "secondary" : "default"}
                size="sm"
                className="h-8 flex-1 min-w-[8rem] text-xs sm:flex-none"
                onClick={() => (canOpenInTracker ? openInTracker() : onStartPursuing?.(grant))}
                disabled={
                  canOpenInTracker
                    ? false
                    : watchlistSaving || pursuingSaving || onPursuing || trackingDisabled
                }
              >
                {pursuingSaving ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : canOpenInTracker ? (
                  <ArrowUpRight className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <PlayCircle className="mr-1.5 h-3.5 w-3.5" />
                )}
                {onPursuing ? (canOpenInTracker ? "Open in tracker" : "In pursuit") : "Start pursuing"}
              </Button>
              <Button
                type="button"
                variant={onSaved ? "secondary" : "outline"}
                size="sm"
                className="h-8 flex-1 min-w-[8rem] text-xs sm:flex-none"
                onClick={() => onSaveToWatchlist?.(grant)}
                disabled={watchlistSaving || pursuingSaving || onSaved || onPursuing || trackingDisabled}
              >
                {watchlistSaving ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : onSaved ? (
                  <BookmarkCheck className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <Bookmark className="mr-1.5 h-3.5 w-3.5" />
                )}
                {onSaved ? "Saved" : "Save for later"}
              </Button>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {showExplainFit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => onExplainFit?.(grant)}
                disabled={explainingFit}
              >
                {explainingFit ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                )}
                Why this fit?
              </Button>
            ) : null}
            {grant.eligibility ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => onSummarizeEligibility(grant)}
                disabled={summarizing}
              >
                {summarizing ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <BookCheck className="mr-1.5 h-3.5 w-3.5" />
                )}
                Who should apply?
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
